import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  CheckCircle,
  Clock,
  Globe,
  AlertCircle,
  Loader,
  Building2,
  CheckSquare,
  ChevronDown,
} from "lucide-react";

const BASE_URL = '';

// ── Common IANA timezones for the dropdown ────────────────────────────────────
const TIMEZONE_OPTIONS = [
  { label: "UTC (UTC+00:00)",                   value: "UTC" },
  { label: "India Standard Time (UTC+05:30)",   value: "Asia/Kolkata" },
  { label: "US Eastern (UTC-05:00)",            value: "America/New_York" },
  { label: "US Central (UTC-06:00)",            value: "America/Chicago" },
  { label: "US Mountain (UTC-07:00)",           value: "America/Denver" },
  { label: "US Pacific (UTC-08:00)",            value: "America/Los_Angeles" },
  { label: "London (UTC+00:00 / BST+01:00)",    value: "Europe/London" },
  { label: "Paris / Berlin (UTC+01:00)",        value: "Europe/Paris" },
  { label: "Dubai (UTC+04:00)",                 value: "Asia/Dubai" },
  { label: "Singapore / KL (UTC+08:00)",        value: "Asia/Singapore" },
  { label: "Tokyo (UTC+09:00)",                 value: "Asia/Tokyo" },
  { label: "Sydney (UTC+10:00)",                value: "Australia/Sydney" },
  { label: "São Paulo (UTC-03:00)",             value: "America/Sao_Paulo" },
  { label: "Johannesburg (UTC+02:00)",          value: "Africa/Johannesburg" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Convert a "yyyy-MM-dd HH:mm:ss" string from one IANA tz to another,
 * returning a human-readable string. Done fully in the browser.
 */
function convertSlot(slotStr, fromTz, toTz) {
  try {
    // Parse "yyyy-MM-dd HH:mm:ss" as a local time in fromTz
    const [datePart, timePart] = slotStr.split(" ");
    const [year, month, day]   = datePart.split("-").map(Number);
    const [hour, minute, sec]  = timePart.split(":").map(Number);

    // Build an ISO string treated as the fromTz local time
    // We use Intl to get the UTC offset for that tz at that moment
    const dateInFromTz = new Date(
      Date.UTC(year, month - 1, day, hour, minute, sec)
    );

    // Get offset of fromTz at this UTC time
    const fromOffset = getOffsetMinutes(dateInFromTz, fromTz);
    // Adjust to real UTC
    const utcMs = dateInFromTz.getTime() - fromOffset * 60000;
    const utcDate = new Date(utcMs);

    // Format in toTz
    return utcDate.toLocaleString("en-US", {
      timeZone: toTz,
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }) + " (" + getShortTzName(utcDate, toTz) + ")";
  } catch {
    return slotStr;
  }
}

function getOffsetMinutes(date, tz) {
  // Returns UTC offset in minutes for a given tz at the given date
  const utcStr  = date.toLocaleString("en-US", { timeZone: "UTC" });
  const tzStr   = date.toLocaleString("en-US", { timeZone: tz });
  return (new Date(tzStr) - new Date(utcStr)) / 60000;
}

function getShortTzName(date, tz) {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value || tz;
  } catch {
    return tz;
  }
}

// ── API calls ─────────────────────────────────────────────────────────────────

const fetchSlots = (applicationId) =>
  axios.get(`${BASE_URL}/api/interview-slots/${applicationId}`);

const confirmSlotApi = (applicationId, selectedSlot, userTimeZone) =>
  axios.post(`${BASE_URL}/api/interview-slots/${applicationId}/select`, {
    selectedSlot,
    userTimeZone,
  });

// ── Main Component ────────────────────────────────────────────────────────────

export default function InterviewSlotPage() {
  const { applicationId } = useParams();

  const [rawData,    setRawData]    = useState(null);   // raw response from API
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed,  setConfirmed]  = useState(false);
  const [confirmedSlot, setConfirmedSlot] = useState(null);

  // Timezone state — starts at client's timezone (from DB), falls back to browser
  const browserTz = getBrowserTimezone();
  const [selectedTz, setSelectedTz] = useState(browserTz);

  // ── Fetch slots once (no timezone param — backend sends raw slots) ──────────
  useEffect(() => {
    if (!applicationId) return;
    setLoading(true);
    fetchSlots(applicationId)
      .then((res) => {
        setRawData(res.data);
        // Default timezone = client's timezone stored in the DB (shown immediately)
        if (res.data.clientTimeZone) {
          setSelectedTz(res.data.clientTimeZone);
        }
        if (res.data.alreadySelected) {
          setConfirmed(true);
          setConfirmedSlot(res.data.selectedSlot);
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Unable to load interview slots.");
      })
      .finally(() => setLoading(false));
  }, [applicationId]);

  // ── Convert slots client-side whenever selectedTz changes ─────────────────
  const clientTz   = rawData?.clientTimeZone || "UTC";
  const rawSlots   = rawData?.slots || [];          // [{original, display, timezone}]

  const convertedSlots = rawSlots.map((slot) => ({
    original: slot.original,
    display:  convertSlot(slot.original, clientTz, selectedTz),
    timezone: selectedTz,
  }));

  // ── Confirm handler ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    try {
      await confirmSlotApi(applicationId, selected, selectedTz);
      setConfirmed(true);
      setConfirmedSlot(convertedSlots.find((s) => s.original === selected)?.display || selected);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to confirm slot. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-200">
            <Loader size={28} className="text-white animate-spin" />
          </div>
          <p className="text-base font-medium text-slate-600">Loading your interview slots…</p>
          <p className="text-sm text-slate-400 mt-1">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // ── ERROR ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full text-center border border-slate-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Slots</h1>
          <p className="text-sm text-slate-500 leading-relaxed">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Please contact your recruiter if this issue persists.</p>
        </div>
      </div>
    );
  }

  // ── ALREADY CONFIRMED ──────────────────────────────────────────────────────
  if (confirmed) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
        <PageHeader candidateName={rawData?.candidateName} selectedTz={selectedTz} slotCount={0} />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={32} className="text-emerald-500" />
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold mb-4">
              <CheckSquare size={12} /> Slot Confirmed
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">You're all set!</h1>
            <p className="text-sm text-slate-500 mb-8 leading-relaxed">
              Hi <span className="font-semibold text-slate-700">{rawData?.candidateName || "there"}</span>,
              your interview slot has been successfully confirmed.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-left mb-5">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Calendar size={12} /> Your Confirmed Slot
              </p>
              <p className="text-base font-bold text-slate-900">{confirmedSlot}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <Globe size={12} className="text-slate-400" />
                <p className="text-xs text-slate-500">{selectedTz}</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  You will receive a calendar invite and further interview instructions via email.
                  If you need to reschedule, please contact your recruiter directly.
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-6">
              Application ID:{" "}
              <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
                {rawData?.applicationId || applicationId}
              </span>
            </p>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  // ── MAIN VIEW ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">

      <PageHeader
        candidateName={rawData?.candidateName}
        selectedTz={selectedTz}
        slotCount={convertedSlots.length}
      />

      <div className="flex-1">
        <div className="max-w-screen-xl mx-auto px-6 py-6 space-y-5">

          {/* ── Timezone selector banner ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Globe size={16} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 mb-0.5">Select Your Timezone</h2>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Slots are shown in your selected timezone. Change the timezone below to see
                  converted times.
                </p>
              </div>
            </div>

            {/* Timezone dropdown */}
            <div className="relative sm:w-72 flex-shrink-0">
              <select
                value={selectedTz}
                onChange={(e) => {
                  setSelectedTz(e.target.value);
                  setSelected(null); // reset selection on tz change
                }}
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 pr-9 text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer"
              >
                {/* If browser tz is not in list, add it at top */}
                {!TIMEZONE_OPTIONS.find((o) => o.value === browserTz) && (
                  <option value={browserTz}>Your local: {browserTz}</option>
                )}
                {TIMEZONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={15}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
          </div>

          {/* ── Slots + confirm panel ── */}
          {convertedSlots.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
              <Calendar size={40} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-700 mb-1">No Slots Available</h3>
              <p className="text-sm text-slate-400">
                There are no interview slots at this time. Please contact your recruiter.
              </p>
            </div>
          ) : (
            <div className="flex gap-5 items-start">

              {/* LEFT — slots list */}
              <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center gap-2">
                  <Calendar size={14} className="text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Available Interview Slots
                  </span>
                  <span className="ml-auto px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-semibold">
                    {convertedSlots.length} option{convertedSlots.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="divide-y divide-slate-100">
                  {convertedSlots.map((slot, idx) => {
                    const isChosen = selected === slot.original;
                    return (
                      <label
                        key={idx}
                        className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors ${
                          isChosen ? "bg-indigo-50/60" : "hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="slot"
                          value={slot.original}
                          checked={isChosen}
                          onChange={() => setSelected(isChosen ? null : slot.original)}
                          className="w-4 h-4 border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                        />

                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors ${
                          isChosen ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"
                        }`}>
                          {idx + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isChosen ? "text-indigo-900" : "text-slate-900"}`}>
                            {slot.display}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <Globe size={10} /> {slot.timezone}
                          </p>
                        </div>

                        {isChosen && <CheckCircle size={18} className="text-indigo-600 flex-shrink-0" />}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT — sticky confirm panel */}
              <div className="w-80 flex-shrink-0 sticky top-24 space-y-4">

                {/* Candidate info */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Your Details</p>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-slate-400">Name</p>
                      <p className="text-sm font-semibold text-slate-900">{rawData?.candidateName || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Selected Timezone</p>
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                        <Globe size={12} className="text-slate-400" /> {selectedTz}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Application ID</p>
                      <p className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-500 mt-0.5 break-all">
                        {rawData?.applicationId || applicationId}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Confirm */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Confirm Selection</p>

                  {selected ? (
                    <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg mb-4">
                      <CheckSquare size={14} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-indigo-600 font-semibold mb-0.5">Selected slot</p>
                        <p className="text-xs text-indigo-800 font-bold leading-relaxed">
                          {convertedSlots.find((s) => s.original === selected)?.display || selected}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
                      <AlertCircle size={13} className="text-slate-400 flex-shrink-0" />
                      <p className="text-xs text-slate-400">No slot selected yet</p>
                    </div>
                  )}

                  <button
                    disabled={!selected || confirming}
                    onClick={handleConfirm}
                    className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm shadow-indigo-200"
                  >
                    {confirming ? (
                      <><Loader size={15} className="animate-spin" /> Confirming…</>
                    ) : (
                      <><CheckCircle size={15} /> Confirm My Slot</>
                    )}
                  </button>
                </div>

                {/* Help */}
                <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Having trouble? Contact your recruiter directly for assistance.
                    </p>
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>

      <PageFooter />
    </div>
  );
}

// ── Shared layout components ──────────────────────────────────────────────────

function PageHeader({ candidateName, selectedTz, slotCount }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
      <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-bold text-slate-900 leading-tight">Interview Slot Portal</h1>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-0.5">
              <span className="flex items-center gap-1"><Globe size={11} /> {selectedTz}</span>
              {slotCount > 0 && (
                <span className="flex items-center gap-1">
                  <Clock size={11} /> {slotCount} slot{slotCount !== 1 ? "s" : ""} available
                </span>
              )}
            </div>
          </div>
        </div>
        {candidateName && (
          <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-semibold flex-shrink-0">
            {candidateName}
          </span>
        )}
      </div>
    </header>
  );
}

function PageFooter() {
  return (
    <footer className="bg-white border-t border-slate-200 flex-shrink-0">
      <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between text-xs text-slate-400">
        <span>Secure candidate portal — your selection is saved instantly.</span>
        <span>Powered by ClustrOps</span>
      </div>
    </footer>
  );
}